import React, { useEffect } from 'react';
import { Page } from '@hud/web-components/Layout/Page';
import { HudIcon } from '@hud/web-components/Icons/HudIcons';
import { DiscoveryType } from '@hud/portal-dtos/webapp-overview.dto';
import useOverviewDiscovery from '../../hooks/overview/useOverviewDiscovery';
import useTimeFrame from '../../hooks/useTimeFrame';
import useEnvironment from '../../hooks/useEnvironment';
import { useQueryParamState } from '../../hooks/useQueryParamState';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import ActionsContainer from '../../components/ActionsContainer/ActionsContainer';
import EnvironmentMenu from '../../components/EnvironmentMenu/EnvironmentMenu';
import TimeFrameMenu from '../../components/TimeFrameMenu/TimeFrameMenu';
import ThemeButton from '../../components/ThemeButton/ThemeButton';
import DiscoveryTableRenderer from './DiscoveryTableRenderer';
import OverviewPageHeader from './OverviewPageHeader';
import { Container } from './OverviewPage.styles';
import OpportunityCarouselRenderer from './OpportunityCarouselRenderer';
import UpsaleCard from './UpsaleCard';
import EntityTotalsRenderer from './EntityTotalsRenderer';

type OverviewPageQueryParams = {
  services: number[];
};

export default function OverviewPage() {
  const { timeFrame } = useTimeFrame();
  const { environment } = useEnvironment();
  const { userPreferredServices } = useUserPreferences();

  const [pageState, setPageState] = useQueryParamState<OverviewPageQueryParams>({
    services: [],
  });

  // on load, by default filter by user preferred services
  useEffect(() => {
    if (!userPreferredServices) return;
    setPageState({ services: userPreferredServices });
  }, [userPreferredServices]);

  useEffect(() => {
    document.title = 'Overview • Hud';
  }, []);

  const environmentName = environment?.name || '';
  const serviceCount = pageState.services?.length || 'All';

  const { data: discoveryData, isLoading: isLoadingDiscovery } = useOverviewDiscovery({
    environmentName,
    timeFrame,
    serviceIds: pageState.services,
  });

  const shouldShowServices = pageState.services.length !== 1;
  return (
    <Page
      fixedHeader={true}
      header={
        <OverviewPageHeader
          services={pageState.services}
          setServices={(services) => setPageState({ services })}
        />
      }
      actions={
        <ActionsContainer>
          <EnvironmentMenu />
          <TimeFrameMenu />
          <ThemeButton />
        </ActionsContainer>
      }
      className="fullHeight"
    >
      <Container>
        <div className="main">
          <section className="entityTotals">
            <h3>
              <div className="iconContainer">
                <HudIcon icon="designSystem.service" size="20px" />
              </div>
              <span>
                What's Running{' '}
                <span className="secondary">({serviceCount} services)</span>
              </span>
            </h3>
            <EntityTotalsRenderer
              timeFrame={timeFrame}
              environmentName={environmentName}
              services={pageState.services}
            />
          </section>
          <section className="fixingOpportunities">
            <h3>
              <div className="iconContainer">
                <HudIcon icon="designSystem.flame" size="20px" />
              </div>
              Fixing Opportunities
            </h3>
            <div className="fixingOpportunities">
              <OpportunityCarouselRenderer
                timeFrame={timeFrame}
                environmentName={environmentName}
                services={pageState.services}
              />
            </div>
          </section>
          <section>
            <h3>
              <div className="iconContainer">
                <HudIcon icon="designSystem.telescope" size="20px" />
              </div>
              Discover how your app behaves
            </h3>
            <div className="tables">
              {discoveryData && (
                <>
                  <DiscoveryTableRenderer
                    isLoading={isLoadingDiscovery}
                    title="Taking the Longest"
                    tooltip="The slowest code path, based on highest P90 duration in the selected timeframe"
                    data={discoveryData.duration}
                    discoveryType={DiscoveryType.Duration}
                    icon={'designSystem.hourglass'}
                    shouldShowServices={shouldShowServices}
                  />
                  <DiscoveryTableRenderer
                    isLoading={isLoadingDiscovery}
                    title="Duration Trends"
                    tooltip="The biggest increase in P90 duration, comparing the last day to the previous week"
                    data={discoveryData.durationTrend}
                    discoveryType={DiscoveryType.DurationTrend}
                    icon={'designSystem.statUp'}
                    shouldShowServices={shouldShowServices}
                  />
                  <DiscoveryTableRenderer
                    isLoading={isLoadingDiscovery}
                    title="Highest Error Rate"
                    tooltip="The most failing code paths, based on highest error rate in the selected timeframe"
                    data={discoveryData.errorRate}
                    discoveryType={DiscoveryType.ErrorRate}
                    icon={'designSystem.exclamationSquare'}
                    shouldShowServices={shouldShowServices}
                  />
                  <DiscoveryTableRenderer
                    isLoading={isLoadingDiscovery}
                    title="Errors Trends"
                    tooltip="The biggest increase in error rate, comparing the last day to the previous week"
                    data={discoveryData.errorRateTrend}
                    discoveryType={DiscoveryType.ErrorRateTrend}
                    icon={'designSystem.statUp'}
                    shouldShowServices={shouldShowServices}
                  />
                  <DiscoveryTableRenderer
                    isLoading={isLoadingDiscovery}
                    title="Called the Most"
                    tooltip="Your most active code paths, based on invocation volume in the selected timeframe"
                    data={discoveryData.invocations}
                    discoveryType={DiscoveryType.Invocations}
                    icon={'designSystem.shield'}
                    shouldShowServices={shouldShowServices}
                  />
                  <DiscoveryTableRenderer
                    isLoading={isLoadingDiscovery}
                    title="Invocations Trends"
                    tooltip="The biggest increase in invocation volume, comparing the last day to the previous week"
                    data={discoveryData.invocationsTrend}
                    discoveryType={DiscoveryType.InvocationsTrend}
                    icon={'designSystem.statUp'}
                    shouldShowServices={shouldShowServices}
                  />
                </>
              )}
            </div>
          </section>
        </div>
        <ul className="overviewSidebar">
          <li>
            <UpsaleCard
              icon={'designSystem.mcp'}
              caption="Power up your AI with real-time production context."
              buttonText="Add Hud MCP"
              url="https://docs.hud.io/docs/hud-mcp-server"
              openExternal={true}
            />
          </li>
          <li>
            <UpsaleCard
              icon={'designSystem.rocket'}
              caption="Experience the value of Hud with live data flowing from your production environment."
              buttonText="Go to Production"
              url="https://docs.hud.io/docs/nodejs-installation-guide"
              openExternal={true}
            />
          </li>
          <li>
            <UpsaleCard
              icon={'designSystem.slack'}
              caption="Stay on top of production issues with alerts sent to your team’s Slack channel."
              buttonText="Get Alerts on Slack"
              url="/settings/slack"
              openExternal={true}
            />
          </li>
          {false && (
            <li>
              <UpsaleCard
                icon={'designSystem.service'}
                caption="Expand your control by connecting another service to Hud."
                buttonText="Expand your next service"
                url="https://docs.hud.io/docs/service-onboarding"
              />
            </li>
          )}
        </ul>
      </Container>
    </Page>
  );
}
